import React, { useState, useEffect, useCallback } from 'react';
import { Settings, LogOut, Users, Camera, Shield } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArshHeader } from './ArshHeader';
import { BACKEND_URL } from '../utils/api';
import { ANIMAL_ICONS } from '../theme';
import { motion, AnimatePresence } from 'framer-motion';
import { vibrate as nativeVibrate } from '../utils/haptics';

/* ─── Alliance List Panel ─── */
const AlliancePanel = ({ open, onClose, allies, count, onNavigate, onMutate }) => {
  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      // Lazy-pull pending requests ONLY when the panel actually opens.
      // No background polling — matches user's zero-cost spec.
      (async () => {
        setLoadingPending(true);
        try {
          const { data } = await axios.get(`${BACKEND_URL}/api/alliance/requests`);
          setPending(data.items || []);
        } catch (_) {
          setPending([]);
        } finally { setLoadingPending(false); }
      })();
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  const accept = async (email) => {
    try {
      await axios.post(`${BACKEND_URL}/api/users/${email}/alliance/accept`, {});
      setPending((p) => p.filter((x) => x.email !== email));
      try { nativeVibrate([60, 30, 60]); } catch (_) {}
      onMutate?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message)
        ? detail.message : 'Could not accept alliance';
      window.alert(msg);
    }
  };
  const reject = async (email) => {
    try {
      await axios.post(`${BACKEND_URL}/api/users/${email}/alliance/reject`, {});
      setPending((p) => p.filter((x) => x.email !== email));
    } catch (_) {}
  };

  if (!open) return null;
  const pendingCount = pending.length;
  return (
    <>
      <div className="fixed inset-0 bg-black z-[9998]" onClick={onClose} />
      <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: '#05050A' }} data-testid="alliance-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#D4AF37]/10">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-[#D4AF37]" />
            <h3 className="font-['Cormorant_Garamond'] text-lg font-light text-white">Alliance</h3>
            <span className="px-2 py-0.5 rounded-full bg-[#D4AF37] text-black text-[10px] font-bold">{count}</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/5"
            data-testid="alliance-panel-close">
            <span className="text-white/30 text-lg">&times;</span>
          </button>
        </div>

        {/* ── Pending Requests (zero-cost pull-only inbox) ── */}
        {(loadingPending || pendingCount > 0) && (
          <div className="px-4 pt-3 pb-2 border-b border-white/5">
            <p className="font-['Outfit'] text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]/70 mb-2 flex items-center gap-2">
              Pending Requests
              {pendingCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                  style={{ background: '#DC143C', color: '#fff' }}>{pendingCount}</span>
              )}
            </p>
            {pending.map((req) => (
              <div key={req.email} className="flex items-center gap-3 py-2"
                data-testid={`alliance-request-${req.email}`}>
                <div className="w-9 h-9 rounded-full overflow-hidden border border-[#D4AF37]/40 flex-shrink-0">
                  {req.profile_picture ? (
                    <img src={req.profile_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-[#05050A] flex items-center justify-center">
                      <span className="text-[#D4AF37] text-xs font-bold">{req.nickname?.charAt(0).toUpperCase() || '?'}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-['Outfit'] text-sm text-white/85 font-semibold truncate">{req.nickname}</p>
                  <p className="font-['Outfit'] text-[10px] text-white/40 uppercase tracking-[0.15em]">{req.profile_tag || 'Kindred'}</p>
                </div>
                <button onClick={() => accept(req.email)}
                  className="px-3 py-1.5 rounded-full font-['Outfit'] text-[10px] uppercase tracking-[0.18em] font-semibold"
                  style={{ background: '#D4AF37', color: '#0a0610' }}
                  data-testid={`alliance-accept-${req.email}`}>
                  Accept
                </button>
                <button onClick={() => reject(req.email)}
                  className="px-3 py-1.5 rounded-full font-['Outfit'] text-[10px] uppercase tracking-[0.18em]"
                  style={{ border: '1px solid #DC143C66', color: '#DC143C99' }}
                  data-testid={`alliance-reject-${req.email}`}>
                  Reject
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {allies.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6">
              <Shield size={36} className="text-white/10 mb-3" />
              <p className="font-['Outfit'] text-sm text-white/30">No alliances yet</p>
              <p className="font-['Outfit'] text-xs text-white/15 mt-1">Form alliances with other users to grow your strength</p>
            </div>
          ) : (
            allies.map((ally, i) => (
              <div key={ally.email || i}
                onClick={() => { onClose(); onNavigate(`/user/${ally.email}`); }}
                className="flex items-center gap-3 px-4 py-3 border-b border-white/5 cursor-pointer hover:bg-[#D4AF37]/5 transition-colors"
                data-testid={`ally-item-${i}`}>
                <div className="w-10 h-10 rounded-full overflow-hidden border border-[#D4AF37]/30 flex-shrink-0"
                  style={{ boxShadow: '0 0 8px rgba(212,175,55,0.15)' }}>
                  {ally.profile_picture ? (
                    <img src={ally.profile_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-[#05050A] flex items-center justify-center">
                      <span className="text-[#D4AF37] text-sm font-bold">{ally.nickname?.charAt(0).toUpperCase() || '?'}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-['Outfit'] text-sm text-white/80 font-semibold">{ally.nickname}</p>
                  {ally.life_quote && <p className="font-['Cormorant_Garamond'] text-xs text-white/30 italic truncate">"{ally.life_quote}"</p>}
                </div>
                <Shield size={14} className="text-[#D4AF37]/40 flex-shrink-0" />
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export const ProfileHeader = ({ user, onSettingsClick }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [showAlliance, setShowAlliance] = useState(false);
  const [allies, setAllies] = useState([]);
  const [allianceCount, setAllianceCount] = useState(user.alliance_count || 0);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchAlliances = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me/alliances`);
      setAllies(data.allies || []);
      setAllianceCount(data.count || 0);
    } catch (_) {}
  }, []);

  /* Lightweight pull of pending alliance requests so we can render a
     red badge on the Alliance icon when someone has asked to ally with
     this user.  Without this badge the recipient has no idea a request
     arrived and the request stays stuck in `pending` forever. */
  const fetchPendingCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/alliance/requests`);
      setPendingCount(data.count || 0);
    } catch (_) {
      setPendingCount(0);
    }
  }, []);

  useEffect(() => {
    fetchPendingCount();
    // Refresh once when the tab regains focus — covers the case where
    // a request arrives while the user is on another tab/app.
    const onFocus = () => fetchPendingCount();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchPendingCount]);

  const handleOpenAlliance = () => {
    fetchAlliances();
    setShowAlliance(true);
  };

  const handleCloseAlliance = () => {
    setShowAlliance(false);
    // Re-pull pending count so the badge updates after accept/reject.
    fetchPendingCount();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0a0a14 0%, #05050A 100%)' }}>
      {/* Subtle radial glow */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.08) 0%, transparent 60%)' }} />

      <div className="relative max-w-2xl mx-auto px-2 pt-2 pb-4">
        {/* #ARSH Gold Header */}
        <ArshHeader />

        {/* ═══ LAYOUT: Scoreboard LEFT | Profile CENTER ═══ */}
        <div className="flex mt-2">
          {/* ─── LEFT SCOREBOARD ─── */}
          <div className="flex flex-col gap-[3px] pt-1 w-[82px] flex-shrink-0" data-testid="imperial-scoreboard">
            {[
              { key: 'fashion', label: 'Fashion' },
              { key: 'food', label: 'Taste' },
              { key: 'explore', label: 'Explore' },
              { key: 'souls', label: 'Soul' },
              { key: 'sporty', label: 'Sport' },
              { key: 'success', label: 'Success' },
              { key: 'legacy', label: 'Legacy' },
            ].map(({ key, label }) => {
              const icon = ANIMAL_ICONS[key];
              const count = user.category_counts?.[key] || 0;
              return (
                <div key={key} className="flex items-center gap-[3px]" data-testid={`score-${key}`}>
                  <div className="w-[18px] h-[18px] rounded-full overflow-hidden flex-shrink-0 border border-[#D4AF37]/20">
                    {icon && <img src={icon.src} alt={label} className="w-full h-full object-cover" style={{ objectPosition: icon.pos }} />}
                  </div>
                  <div className="flex-1 min-w-0 leading-none">
                    <span className="font-['Outfit'] text-[7px] text-white/35 block truncate">{label}</span>
                    <span className="font-['Outfit'] text-[9px] text-[#D4AF37] font-bold">{count}</span>
                  </div>
                </div>
              );
            })}
            {/* Alliance row — Ruby gemstone */}
            <div className="flex items-center gap-[3px] mt-[2px] pt-[3px] border-t border-[#D4AF37]/10" data-testid="score-alliance">
              <div className="w-[18px] h-[18px] rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #9B111E, #6B0A14)', border: '1px solid rgba(220,20,60,0.4)' }}>
                <svg viewBox="0 0 20 20" width="11" height="11">
                  <path d="M10 2 L13 8 L10 18 L7 8 Z" fill="#DC143C" stroke="#FF4060" strokeWidth="0.5" opacity="0.9"/>
                  <path d="M10 2 L13 8 L10 10" fill="#FF6080" opacity="0.3"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0 leading-none">
                <span className="font-['Outfit'] text-[7px] text-white/35 block">Alliance</span>
                <span className="font-['Outfit'] text-[9px] font-bold" style={{ color: '#DC143C' }}>{user.alliance_count || 0}</span>
              </div>
            </div>
          </div>

          {/* ─── CENTER PROFILE ─── */}
          <div className="flex-1 flex flex-col items-center text-center">
            {/* Profile image with gold ring */}
            <div className="relative group mb-3">
              <div
                className="w-28 h-28 rounded-full overflow-hidden border-2 border-[#D4AF37]/30"
                style={{ boxShadow: '0 0 25px rgba(212,175,55,0.15)' }}
              >
                {user.profile_picture ? (
                  <img src={user.profile_picture} alt={user.nickname} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/30 to-[#05050A] flex items-center justify-center">
                    <span className="font-['Cormorant_Garamond'] text-3xl text-[#D4AF37]">
                      {user.nickname?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={onSettingsClick}
                className="absolute bottom-0 right-0 w-7 h-7 bg-[#D4AF37] text-black rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300"
                data-testid="edit-profile-picture"
              >
                <Camera size={12} />
              </button>
            </div>

            <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-white tracking-wide" data-testid="profile-nickname">
              {user.nickname}
            </h2>

            {user.life_quote && (
              <p className="font-['Cormorant_Garamond'] italic text-sm text-white/40 mt-1 max-w-md" data-testid="profile-life-quote">
                "{user.life_quote}"
              </p>
            )}

          </div>

          {/* ─── RIGHT ACTIONS ─── */}
          <div className="flex flex-col gap-1 pt-1 w-[82px] flex-shrink-0" data-testid="profile-actions-right">
            <button onClick={() => navigate('/discover')}
              className="flex items-center gap-1 w-full hover:bg-[#D4AF37]/5 rounded px-1 py-1 transition-colors"
              data-testid="profile-discover-button">
              <Users size={15} className="text-[#D4AF37]/50 flex-shrink-0" />
              <span className="font-['Outfit'] text-[9px] text-white/40">Discover</span>
            </button>
            <button onClick={handleOpenAlliance}
              className="relative flex items-center gap-1 w-full hover:bg-[#D4AF37]/5 rounded px-1 py-1 transition-colors"
              data-testid="profile-alliance-button">
              <Shield size={15} className="text-[#D4AF37]/50 flex-shrink-0" />
              <span className="font-['Outfit'] text-[9px] text-white/40">Alliance</span>
              {pendingCount > 0 && (
                <span className="absolute -top-0.5 -left-0.5 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center font-['Outfit'] font-bold text-[9px] text-white"
                  style={{
                    background: 'linear-gradient(135deg, #DC143C, #8a0a20)',
                    boxShadow: '0 0 6px rgba(220,20,60,0.7), inset 0 0 0 1px rgba(255,80,100,0.6)',
                  }}
                  data-testid="alliance-pending-badge">
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              )}
            </button>
            <button onClick={onSettingsClick}
              className="flex items-center gap-1 w-full hover:bg-[#D4AF37]/5 rounded px-1 py-1 transition-colors"
              data-testid="profile-settings-button">
              <Settings size={15} className="text-[#D4AF37]/50 flex-shrink-0" />
              <span className="font-['Outfit'] text-[9px] text-white/40">Settings</span>
            </button>
            <button onClick={handleLogout}
              className="flex items-center gap-1 w-full hover:bg-red-500/5 rounded px-1 py-1 transition-colors"
              data-testid="profile-logout-button">
              <LogOut size={15} className="text-white/25 flex-shrink-0" />
              <span className="font-['Outfit'] text-[9px] text-white/25">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom border glow */}
      <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

      <AlliancePanel open={showAlliance} onClose={handleCloseAlliance}
        allies={allies} count={allianceCount} onNavigate={navigate}
        onMutate={() => {
          fetchAlliances();
          fetchPendingCount();
          window.dispatchEvent(new Event('alliance:refetch'));
        }}/>
    </div>
  );
};
